/**
 * Vehicle trade data wizard make/model section
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik, setNestedObjectValues } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';

/**
 * Redux
 */
import { useSelector, useDispatch } from 'react-redux';

/**
 * Api
 */
import HttpStatus from 'http-status-codes';
import api from '../../../helpers/api';

/**
 * Redux slices
 */
import {
  lookupsState, saveQuoteModels,
  saveQuoteMakes, saveQuoteVersions,
  resetQuoteLookup, saveQuoteVersionsLoad,
} from '../../../redux/lookupsSlice';
import {
  leadState, updateQuoteData, updateQuoteStepValidation,
} from '../../../redux/leadSlice';
import { validateForm, wizardState } from '../../../redux/wizardSlice';

/**
 * Components
 */
import InputSelect from '../../../components/form/InputSelect';

/**
 * @returns {JSX.Element}
 */
function MakeModel() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { quote, cookie } = useSelector(leadState);
  const { validateForm: needValidate } = useSelector(wizardState);
  const { makeId, modelId, stateRegistrationYear } = quote;
  const { quote: quoteLookups } = useSelector(lookupsState);
  const { makes: makesLookup, models: modelsLookup } = quoteLookups;
  const initialData = {
    make: typeof makeId === 'string' ? makeId : '',
    model: typeof modelId === 'string' ? modelId : '',
    registrationYear: typeof stateRegistrationYear === 'string' ? stateRegistrationYear : '',
  };

  const [lookupDataLoading, setLookupDataLoading] = useState(false);
  const [registrationYearsLookup, setRegistrationYearsLookup] = useState([]);

  const module = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    initialValues: initialData,
    validationSchema: Yup.object().shape({
      make: Yup.string().required(),
      model: Yup.string().test({
        name: 'model',
        message: 'Select a model',
        exclusive: false,
        test(val, context) {
          const { make } = context.parent;
          if (!make || typeof make === 'undefined') {
            return true;
          }
          return !!val;
        },
      }),
      registrationYear: Yup.string().test({
        name: 'registrationYear',
        message: 'Select a year',
        exclusive: false,
        test(val, context) {
          const { make, model } = context.parent;
          if ((!make || typeof make === 'undefined') || (!model || typeof model === 'undefined')) {
            return true;
          }
          return !!val;
        },
      }),
    }),
  });

  /**
   * Prepare registration years lookup
   */
  const getRegistrationYearsLookup = (models, currentModel, setValueFromCookie = false) => {
    if (currentModel !== '') {
      let years = [];
      for (let i = 0; i < models.length; i += 1) {
        if (models[i].id === currentModel) {
          years = models[i].years;
        }
      }
      const yearsValues = [...years].sort().reverse();
      setRegistrationYearsLookup(yearsValues.map((year) => ({ text: year, value: year })));
      if (years.length === 1) {
        module.setFieldValue('registrationYear', String(yearsValues[0]));
      }
      if (setValueFromCookie) {
        module.setFieldValue('registrationYear', cookie.stateRegistrationYear);
      }
    } else {
      setRegistrationYearsLookup([]);
    }
  };

  /**
   * Fetch makes lookup
   */
  const getQuoteMakesLookup = async () => {
    setLookupDataLoading(true);

    const res = await api.get({
      entryPoint: '/infocar/makes',
      silentFail: false,
      pgp: true,
      auth: false,
    });

    if (res?.status === HttpStatus.OK) {
      dispatch(saveQuoteMakes(res.data.result.values));
    }

    setLookupDataLoading(false);
  };

  /**
   * Fetch models lookup
   */
  const getModelsLookup = async (currentMake, setValueFromCookie = false) => {
    setLookupDataLoading(true);

    const res = await api.get({
      entryPoint: `/infocar/makes/${currentMake}/models`,
      silentFail: false,
      pgp: true,
      auth: false,
    });

    if (res?.status === HttpStatus.OK) {
      dispatch(saveQuoteModels(res.data.result.values));
      if (res.data.result.values.length === 1) {
        const modelValue = res.data.result.values[0].id;
        module.setFieldValue('model', modelValue);
        getRegistrationYearsLookup(res.data.result.values, modelValue);
      }
      if (setValueFromCookie) {
        module.setFieldValue('model', cookie.modelId);
        getRegistrationYearsLookup(res.data.result.values, cookie.modelId, true);
      }
    }

    setLookupDataLoading(false);
  };

  /**
   * Fetch versions lookup
   */
  const getVersionsLookup = async (currentMake, currentModel, currentRegistrationYear) => {
    setLookupDataLoading(true);
    dispatch(saveQuoteVersionsLoad(true));

    const res = await api.get({
      entryPoint: `/infocar/makes/${currentMake}/models/${currentModel}/${currentRegistrationYear}`,
      silentFail: false,
      pgp: true,
      auth: false,
      params: {
        make_id: currentMake,
        model_id: currentModel,
        year: currentRegistrationYear,
      },
    });

    if (res?.status === HttpStatus.OK) {
      dispatch(saveQuoteVersions(res.data.result));
    }
    dispatch(saveQuoteVersionsLoad(false));

    setLookupDataLoading(false);
  };

  useEffect(() => {
    getQuoteMakesLookup();
  }, []);

  useEffect(() => {
    dispatch(updateQuoteStepValidation({ idx: 0, isValid: module.isValid }));
  }, [module.isValid, module.dirty]);

  useEffect(() => {
    if (cookie && cookie.modelId !== '' && typeof cookie.modelId !== 'undefined' && cookie.modelId === quote.modelId) {
      getModelsLookup(cookie.makeId, true);
    }
  }, [JSON.stringify(cookie)]);

  useEffect(() => {
    const { make } = module.values;

    if (make !== makeId) {
      const quoteData = {
        makeId: make,
        makeText: '',
        modelId: '',
        modelText: '',
        stateRegistrationYear: '',
      };
      for (let i = 0; i < makesLookup.length; i += 1) {
        if (makesLookup[i].id === make) {
          quoteData.makeText = makesLookup[i].name;
        }
      }
      module.setFieldValue('model', '');
      if (make) {
        getModelsLookup(make);
      }
      dispatch(updateQuoteData(quoteData));
    }
  }, [module.values.make]);

  useEffect(() => {
    const { model } = module.values;
    if (!model || !modelsLookup.length) {
      dispatch(updateQuoteData({ modelId: '', modelText: '', stateRegistrationYear: '' }));
      return;
    }
    let modelText = '';
    for (let i = 0; i < modelsLookup.length; i += 1) {
      if (modelsLookup[i].id === model) {
        modelText = modelsLookup[i].name;
      }
    }
    const data = {
      modelId: model,
      modelText,
    };
    if (model !== quote.modelId) {
      data.stateRegistrationYear = '';
    }
    dispatch(updateQuoteData(data));

    getRegistrationYearsLookup(modelsLookup, model);
  }, [module.values.model]);

  useEffect(() => {
    const { registrationYear: ry, model, make } = module.values;
    if (!ry) {
      dispatch(resetQuoteLookup({ id: 'versions' }));
      dispatch(updateQuoteData({ stateRegistrationYear: '' }));
      return;
    }
    dispatch(updateQuoteData({ stateRegistrationYear: ry }));

    if (ry && model && make) {
      getVersionsLookup(make, model, ry);
    }
  }, [module.values.registrationYear]);

  useEffect(() => {
    const { registrationYear: moduleRy, model: moduleModel, make: moduleMake } = module.values;
    const { stateRegistrationYear: ry, modelId: model, makeId: make } = quote;
    const moduleObj = { year: moduleRy, model: moduleModel, make: moduleMake };
    const quoteObj = { year: ry, model, make };
    if (
      moduleRy && moduleModel && moduleMake && ry
      && model && make && !_.isEqual(moduleObj, quoteObj)
    ) {
      dispatch(updateQuoteData({
        techFuel: '',
        techFuelText: '',
        techBody: '',
        techBodyText: '',
        techDoors: '',
        techDoorsText: '',
        techGearbox: '',
        techGearboxText: '',
        techDisplacement: '',
        techDisplacementText: '',
        techPower: '',
        techPowerText: '',
        versionId: '',
        versionText: '',
        versionCustom: '',
      }));
    }
  }, [JSON.stringify(quote), JSON.stringify(module.values)]);

  useEffect(() => {
    async function validate() {
      if (needValidate) {
        const validationErrors = await module.validateForm();
        if (Object.keys(validationErrors).length > 0) {
          await module.setTouched(setNestedObjectValues(validationErrors, true));
        }
        dispatch(validateForm(false));
      }
    }
    validate();
  }, [needValidate]);

  return (
    <>
      <InputSelect
        formik={module}
        items={makesLookup}
        label={`${t('searchv3.title.make')} *`}
        errorMessage={`${t('Select the make')}.`}
        name="make"
        disabled={makesLookup.length === 0}
        style={{ marginBottom: 24 }}
        debounceTime={0}
      />
      <InputSelect
        formik={module}
        items={modelsLookup}
        label={`${t('searchv3.title.model')} *`}
        errorMessage={`${t('Select the model')}.`}
        name="model"
        disabled={
          module.values.make === ''
          || modelsLookup.length === 0
          || lookupDataLoading
        }
        style={{ marginBottom: 24 }}
        debounceTime={0}
      />
      <InputSelect
        formik={module}
        items={registrationYearsLookup}
        label={`${t('Registration year full')} *`}
        errorMessage={`${t('Please select a year.')}.`}
        name="registrationYear"
        disabled={
          module.values.model === ''
          || registrationYearsLookup.length === 0
          || lookupDataLoading
        }
        style={{ marginBottom: 24 }}
        debounceTime={0}
      />
    </>
  );
}

export default MakeModel;
